{
  "title": "How to Export a Report from the Export Center",
  "description": "Pick a report kind, columns, and filters in the Export Center, then download it as CSV or convert it to XLSX.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, you'll have downloaded the report you need as CSV or XLSX, and you'll know how to check its audit record on the same page.",
  "roleNote": "Competition Admin / staff with access to the matching data (the scores report needs score.read)",
  "prerequisites": [
    "The competition has been created",
    "The data the report covers already exists (for example, a teams report with no teams yet will only export an empty header row)"
  ],
  "pathLabel": "Organizer Dashboard → My Competition → Data → Export Center",
  "steps": [
    {
      "title": "1. Open the Export Center",
      "description": "From the competition, open \"Export Center\" under the \"Data\" nav group."
    },
    {
      "title": "2. Pick a report kind",
      "description": "The page offers 7 report kinds — teams, submissions, participants, eligibility, registration, judging, and scores & review comments — each its own card. \"Scores & review comments\" has one row per judge per submission (round, category, team, submission ID, judge, status, total, criterion scores, review comment, submitted at), lists submitted scores only, and needs score-viewing access (score.read), which competition admins and staff both hold."
    },
    {
      "title": "3. Select columns (optional)",
      "description": "Each card starts with every column for that report checked; uncheck any you don't need. At least one column must stay checked to export. A single report exports at most 50,000 rows; above that you are asked to narrow it with filters."
    },
    {
      "title": "4. Apply your current filters (optional)",
      "description": "The eligibility and registration reports support an \"Apply current filters\" checkbox, letting you narrow the export by status, team, keyword, and similar; the teams, submissions, participants, and judging reports currently have no filters and always export every row."
    },
    {
      "title": "5. Confirm the export scope",
      "description": "Right above the download button, every card shows a live \"Will export N rows\" (computed from your current columns and filters) plus a \"Current filters: ...\" summary (\"None\" when there are none). Check this before you download to confirm the scope is what you expect — this count query itself is never recorded as an export."
    },
    {
      "title": "6. Download CSV or convert to XLSX",
      "description": "Click \"Download CSV\" to get the raw CSV file directly, or click \"Convert to XLSX\" to convert the same data to an Excel file in your browser; both contain identical data — only the file format differs."
    }
  ],
  "afterward": [
    "The downloaded file appears in your browser's downloads folder",
    "The eligibility and registration reports deliberately never include the proof file itself, a private object key, a signed URL, or raw form-answer content",
    "The scores & review comments report contains only the review comment meant for organizers, never a judge's private scoring note, and excludes drafts and reopened scores",
    "This export (along with the existing teams and submissions exports) immediately shows up in the same page's \"Export History\", recording the actor, report kind, filters, row count, and time"
  ],
  "nextSteps": "If you need to export the same report regularly, note down the columns and filters you picked and reapply them next time — there's currently no saved-template feature.",
  "faq": [
    {
      "q": "Why don't the eligibility and registration reports include the proof file or raw form content?",
      "a": "That's by design: the allowed column list for those two reports never includes the proof file itself, a private object key, a signed URL, or raw form-answer content, and the server independently re-validates this too — even calling the API directly can't retrieve them."
    },
    {
      "q": "How long is export history kept? Can I delete an entry?",
      "a": "Export history is kept read-only, like every other audit record — there's no delete or edit action. Every successful export leaves behind the actor, report kind, filters, row count, and time."
    },
    {
      "q": "Does the live \"Will export N rows\" count itself show up in export history?",
      "a": "No. That count query is deliberately never audited — only actually clicking \"Download CSV\" or \"Convert to XLSX\" shows up in Export History, so adjusting filters doesn't flood the audit trail with non-export queries."
    }
  ]
}
